import { AlertTriangle, Check, ExternalLink, Bell, Megaphone, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Separator } from '@reflex/ui/components/ui/separator';
import { timeAgo, formatDateTime } from '@reflex/ui/lib/format';
import { cn } from '@reflex/ui/lib/utils';
import { useAuth } from '@reflex/ui/client/context';
import { canFollowNotificationHref } from '../hrefEntitlement.js';
import { notificationHeadingFor } from '../notificationHeading.js';
import { NotificationBody } from './renderers';
import { ReleaseTypeBadge } from './ReleaseTypeBadge';
import type { Notification, NotificationType } from '../../shared/types.js';

const TYPE_META: Record<NotificationType, { label: string; icon: typeof Bell; accent: string }> = {
  generic_markdown: { label: 'Announcement', icon: Megaphone, accent: 'text-blue-400' },
  version_release: { label: 'New Release', icon: Tag, accent: 'text-muted-foreground' },
  release_event: { label: 'New Release', icon: Tag, accent: 'text-muted-foreground' },
  plugin_disabled: { label: 'Plugin disabled', icon: AlertTriangle, accent: 'text-destructive' },
};

function isReleaseType(type: NotificationType): boolean {
  return type === 'version_release' || type === 'release_event';
}

export interface NotificationContentProps {
  notification: Notification;
  onDismiss?: () => void;
  isDismissing?: boolean;
  className?: string;
}

/**
 * Shared notification rendering used by both the inbox detail panel and the
 * full-page notification view. Ensures consistent layout regardless of context.
 */
export function NotificationContent({
  notification,
  onDismiss,
  isDismissing,
  className,
}: NotificationContentProps) {
  const meta = TYPE_META[notification.type];
  const TypeIcon = meta.icon;
  const auth = useAuth();
  // A server-authored `href` may point into the admin console. Readers who
  // cannot open it get no button rather than one that lands on "access
  // denied" — see `canFollowNotificationHref`.
  const href =
    notification.href && canFollowNotificationHref(notification.href, auth)
      ? notification.href
      : null;
  // Title and subtitle are stored as the server wrote them, for the audience
  // it addressed. A reader who cannot act on an operator row gets a heading
  // scoped to what it means for them — see `notificationHeadingFor`.
  const { title, subtitle } = notificationHeadingFor(notification, auth);

  return (
    <div className={cn('w-full max-w-2xl', className)} data-testid="notification-content">
      {/* Header: icon + category + timestamp. The left group and timestamp
          must be able to shrink/wrap: this renders inside a ScrollArea whose
          viewport sizes to min-content, so a non-wrapping full datetime would
          push the whole panel wider than a phone viewport and clip the
          Dismiss button off-screen. */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-muted/40',
              meta.accent,
            )}
          >
            <TypeIcon className="h-5 w-5" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <Badge variant="outline" className="w-fit text-3xs">
              {meta.label}
            </Badge>
            <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
              <span>{timeAgo(notification.createdAt)}</span>
              <span className="text-border">·</span>
              <time dateTime={new Date(notification.createdAt).toISOString()}>
                {formatDateTime(notification.createdAt)}
              </time>
            </div>
          </div>
        </div>

        {/* Dismiss / dismissed state */}
        <div className="shrink-0">
          {!notification.dismissed ? (
            onDismiss && (
              <Button
                variant="outline"
                size="sm"
                onClick={onDismiss}
                disabled={isDismissing}
                data-testid="notification-dismiss-button"
              >
                <Check className="icon-sm" aria-hidden="true" />
                Dismiss
              </Button>
            )
          ) : (
            <Badge variant="secondary" data-testid="notification-dismissed-label">
              Dismissed
            </Badge>
          )}
        </div>
      </div>

      <Separator className="my-5" />

      {/* Title + subtitle */}
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}

      {/* Release-type chip. The title already carries the version, so the
          release_event body no longer repeats it — the type badge lives here
          alongside the title instead. */}
      {notification.payload.type === 'release_event' && (
        <div className="mt-3">
          <ReleaseTypeBadge releaseType={notification.payload.releaseType} />
        </div>
      )}

      {/* Version tag for legacy version_release rows. The release_event
          renderer shows its own release-type badge + version, so we skip this
          block for it to avoid a duplicate version display. */}
      {notification.type === 'version_release' && (
        <div className="mt-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Tag className="h-4 w-4" />
          </div>
          <span className="font-mono text-base font-semibold tracking-tight">
            v{(notification.payload as { version: string }).version}
          </span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            Latest
          </span>
        </div>
      )}

      {/* Body */}
      {isReleaseType(notification.type) ? (
        <div className="mt-5">
          <NotificationBody notification={notification} />
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-border/50 bg-card/50 p-5">
          <NotificationBody notification={notification} />
        </div>
      )}

      {/* Footer links */}
      {href && (
        <div className="mt-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to={href}>
              <ExternalLink className="icon-sm" aria-hidden="true" />
              Open link
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
